import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "./ui/button";
import { Camera, CameraOff, Copy, ExternalLink, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { addHistoryItem } from "../lib/qrHistory";

const REGION_ID = "qr-reader";

export default function QRScanner({ onHistoryChange }) {
  const [isScanning, setIsScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const scannerRef = useRef(null);
  const lastResultRef = useRef("");

  useEffect(() => {
    return () => {
      stopScan();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startScan = async () => {
    setError("");
    setResult(null);
    lastResultRef.current = "";
    try {
      const scanner = new Html5Qrcode(REGION_ID, { verbose: false });
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: (viewW, viewH) => {
            const side = Math.floor(Math.min(viewW, viewH) * 0.7);
            return { width: side, height: side };
          },
          aspectRatio: 1.0,
        },
        (decodedText) => {
          if (decodedText && decodedText !== lastResultRef.current) {
            lastResultRef.current = decodedText;
            setResult(decodedText);
            const saved = addHistoryItem({
              kind: "scan",
              type: detectType(decodedText),
              value: decodedText,
            });
            onHistoryChange && onHistoryChange(saved);
            toast.success("QR kod okundu");
            stopScan();
          }
        },
        () => {
          /* ignore per-frame decode errors */
        }
      );
      setIsScanning(true);
    } catch (e) {
      console.error(e);
      setError(
        "Kameraya erişilemedi. Lütfen tarayıcı izinlerini kontrol edin veya HTTPS üzerinden açtığınızdan emin olun."
      );
      setIsScanning(false);
    }
  };

  const stopScan = async () => {
    try {
      if (scannerRef.current) {
        const state = scannerRef.current.getState && scannerRef.current.getState();
        if (state === 2 /* SCANNING */) {
          await scannerRef.current.stop();
        }
        await scannerRef.current.clear();
        scannerRef.current = null;
      }
    } catch {
      // ignore cleanup errors
    } finally {
      setIsScanning(false);
    }
  };

  const copyResult = async () => {
    if (!result) return;
    await navigator.clipboard.writeText(result);
    toast.success("Panoya kopyalandı");
  };

  const isUrl = result && /^(https?:\/\/|www\.)/i.test(result);

  return (
    <div className="flex flex-col gap-6">
      <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-950 aspect-square sm:aspect-[4/3] w-full">
        <div id={REGION_ID} className="w-full h-full" />

        {/* Overlay */}
        {!isScanning && !result && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 bg-slate-950 text-white p-6 text-center">
            <div className="w-16 h-16 rounded-2xl bg-blue-600/15 border border-blue-500/30 flex items-center justify-center">
              <Camera className="w-8 h-8 text-blue-400" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-display text-xl font-semibold">
                Kameranız hazır
              </h3>
              <p className="text-sm text-slate-300 mt-1 max-w-xs">
                QR kodu kameraya doğrultun; otomatik olarak okunacak.
              </p>
            </div>
            <Button
              data-testid="camera-start-button"
              onClick={startScan}
              className="rounded-full bg-blue-600 hover:bg-blue-700 text-white px-7 py-6 text-base font-semibold active:scale-95 transition-all"
            >
              <Camera className="w-5 h-5 mr-2" />
              Kamerayı Aç
            </Button>
          </div>
        )}

        {isScanning && (
          <>
            {/* Scanning brackets overlay */}
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center z-10">
              <div className="relative w-[70%] aspect-square">
                <div className="absolute -top-1 -left-1 w-10 h-10 border-l-4 border-t-4 border-blue-500 rounded-tl-xl pulse-corners" />
                <div className="absolute -top-1 -right-1 w-10 h-10 border-r-4 border-t-4 border-blue-500 rounded-tr-xl pulse-corners" />
                <div className="absolute -bottom-1 -left-1 w-10 h-10 border-l-4 border-b-4 border-blue-500 rounded-bl-xl pulse-corners" />
                <div className="absolute -bottom-1 -right-1 w-10 h-10 border-r-4 border-b-4 border-blue-500 rounded-br-xl pulse-corners" />
                <div className="absolute inset-0 overflow-hidden rounded-xl">
                  <div className="scan-line h-[2px] bg-gradient-to-r from-transparent via-blue-400 to-transparent" />
                </div>
              </div>
            </div>

            <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center z-10">
              <div className="backdrop-blur-md bg-white/90 text-slate-900 px-4 py-2 rounded-full text-sm font-medium shadow-lg flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
                QR kodu çerçevenin içine hizalayın
              </div>
            </div>
          </>
        )}

        {result && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-slate-950/95 backdrop-blur-sm text-white p-6 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-400" />
            </div>
            <h3 className="font-display text-lg font-semibold">Okundu</h3>
            <div
              data-testid="scan-result-value"
              className="max-w-md break-all bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm font-mono"
            >
              {result}
            </div>
          </div>
        )}
      </div>

      {isScanning && (
        <Button
          data-testid="camera-stop-button"
          onClick={stopScan}
          variant="outline"
          className="rounded-full border-slate-300 h-12 font-semibold active:scale-95 transition-all"
        >
          <CameraOff className="w-4 h-4 mr-2" />
          Taramayı Durdur
        </Button>
      )}

      {result && (
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            data-testid="scan-copy-button"
            onClick={copyResult}
            className="flex-1 rounded-full bg-blue-600 hover:bg-blue-700 text-white h-12 font-semibold active:scale-95 transition-all"
          >
            <Copy className="w-4 h-4 mr-2" />
            Kopyala
          </Button>
          {isUrl && (
            <Button
              data-testid="scan-open-url-button"
              asChild
              variant="outline"
              className="flex-1 rounded-full h-12 font-semibold border-slate-300 active:scale-95 transition-all"
            >
              <a
                href={result.startsWith("http") ? result : `https://${result}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Bağlantıyı Aç
              </a>
            </Button>
          )}
          <Button
            data-testid="scan-again-button"
            onClick={startScan}
            variant="outline"
            className="flex-1 rounded-full h-12 font-semibold border-slate-300 active:scale-95 transition-all"
          >
            <Camera className="w-4 h-4 mr-2" />
            Tekrar Tara
          </Button>
        </div>
      )}

      {error && (
        <div
          data-testid="scan-error"
          className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        >
          {error}
        </div>
      )}
    </div>
  );
}

function detectType(text) {
  if (/^https?:\/\//i.test(text) || /^www\./i.test(text)) return "url";
  if (/^WIFI:/i.test(text)) return "wifi";
  if (/^mailto:/i.test(text)) return "email";
  if (/^tel:/i.test(text)) return "phone";
  if (/^SMSTO:/i.test(text) || /^sms:/i.test(text)) return "sms";
  return "text";
}
